import React from 'react';
import styled, { css } from 'styled-components';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Body, Heading4 } from '~/components/atom/Typography';
import Toolbar from '~/components/molecule/Toolbar';
import Button from '~/components/atom/Button';
import TEST_ID from './index.testid';
import type { CopyPhase } from '~/components/page/Automation/v2/state/interactions';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import interactions from '~/components/page/Automation/v2/state/interactions';
import {
  actionsSelector,
  flowActions,
} from '~/components/page/Automation/v2/state/actions';
import getCardLabel from '~/components/page/Automation/v2/components/Builder/components/nodeTypes/components/CardTemplate/utils/getCardLabel';
import useBuilderContext from '~/components/page/Automation/v2/components/Builder/hooks/useBuilderContext';
import getTargetIdFromSourcePortal, {
  isSourcePortalId,
} from '../../../../utils/getTargetIdFromSourcePortal';
import { clone } from 'ramda';
import ActionLabel from '../../../RepresentationLabel/components/ActionLabel';

const text = {
  doneSelecting: 'Klaar met selecteren',
  cancel: 'Annuleren',
  heading: {
    selecting: 'Selecteer de acties die je wilt kopiëren',
    pasting: 'Selecteer waar je de acties wilt invoegen',
    choosingPathToConnect:
      'Selecteer een pad vanuit de gekopieerde branch om te verbinden',
  },
  copiedItemLabel: 'Gekopieerde acties:',
  selectingLabel: 'Geselecteerde acties om te kopiëren:',
  pastingAction: 'Gekopieerde actie:',
  pastingActions: (count: number) => `Gekopieerde acties (${count}):`,
};

export type Props = {
  /**
   * Current phase of the copy interaction.
   * 1. selecting: The user is selecting actions to copy.
   * 2. pasting: The user has selected actions to copy and is pasting them.
   * 3. choosingPathToConnect: The user has selected actions to copy and is choosing the path from the leaf node to connect the target action.
   */
  phase: CopyPhase;

  /**
   * Name for the item that is copied
   */
  copiedItemLabel: React.ReactNode;

  /**
   * Called to transition from selecting -> pasting, or to finalize pasting
   */
  onPrimaryAction: () => void;

  /**
   * Called to cancel copying
   */
  onCancel: () => void;
};

const Content: React.FCC<Props> = ({
  phase = 'pasting',
  copiedItemLabel,
  onPrimaryAction,
  onCancel,
  ...rest
}) => (
  <StyledToolbar dataTestId={TEST_ID.CONTAINER} {...rest}>
    <JustificationContainer
      direction="column"
      width="100%"
      gap="m"
      padding={['base']}
    >
      <Heading4
        margin={[null]}
        size="m"
        color={{ group: 'primary', variant: 'light' }}
      >
        {text.heading[phase]}
      </Heading4>

      <ChoosePathMessage />

      <JustificationContainer width="100%" gap="m">
        <Body margin={[null]} size="base">
          {text.copiedItemLabel}
        </Body>
        {copiedItemLabel}
      </JustificationContainer>
    </JustificationContainer>

    <Button
      size="medium"
      label={text.cancel}
      ghost
      margin={[null, 'base', null, null]}
      onClick={onCancel}
      dataTestId={TEST_ID.STOP_COPYING_BUTTON}
    />
    {phase === 'selecting' && (
      <Button
        size="medium"
        label={text.doneSelecting}
        onClick={onPrimaryAction}
        dataTestId={TEST_ID.PRIMARY_ACTION_BUTTON}
      />
    )}
  </StyledToolbar>
);

const CopyStateToolbar: React.FCC<{ hasChanges: boolean }> = ({
  hasChanges,
}) => {
  const [currentInteraction, setInteraction] = useRecoilState(interactions);
  const setActions = useSetRecoilState(flowActions);

  if (!currentInteraction || currentInteraction.type !== 'copyAction')
    return null;

  const { phase, copiedActions } = currentInteraction;

  return (
    <Content
      phase={phase}
      copiedItemLabel={
        <JustificationContainer align="center" gap="xxs" wrap="wrap">
          {copiedActions.map((action, index) => (
            <React.Fragment key={action.id}>
              {index > 0 && ','}
              <ActionLabel label={getCardLabel(action.label)} margin={[null]} />
            </React.Fragment>
          ))}
        </JustificationContainer>
      }
      onPrimaryAction={() => {
        setInteraction({
          ...currentInteraction,
          phase: 'pasting',
        });
      }}
      onCancel={() => {
        if (hasChanges) {
          setActions(clone(currentInteraction.actionsBeforeCopy));
        }
        setInteraction(null);
      }}
    />
  );
};

const ChoosePathMessage: React.FCC = () => {
  const currentInteraction = useRecoilValue(interactions);
  const builderContext = useBuilderContext();
  const flowActions = useRecoilValue(
    actionsSelector({
      accountId: builderContext.accountId,
      flowBlueprintId: builderContext.flowBlueprintId,
    }),
  );

  const targetActionInchoosingPathToConnectPhase =
    currentInteraction?.type === 'copyAction' &&
    currentInteraction?.phase === 'choosingPathToConnect'
      ? currentInteraction?.pastePoint?.target
      : null;

  const isPortal =
    targetActionInchoosingPathToConnectPhase != null &&
    isSourcePortalId(targetActionInchoosingPathToConnectPhase);
  const targetActionId = isPortal
    ? getTargetIdFromSourcePortal(
        targetActionInchoosingPathToConnectPhase ?? '',
      )
    : targetActionInchoosingPathToConnectPhase;

  const targetAction = flowActions.find(a => a.id === targetActionId);

  if (!targetAction) return null;

  return (
    <Body margin={[null]}>
      Selecteer een pad vanuit de gekopieerde branch om met{' '}
      {getCardLabel(targetAction.label)} te verbinden
    </Body>
  );
};

const StyledToolbar = styled(Toolbar)(
  ({ theme }) => css`
    border: 2px solid ${theme.color('primary', 'light')};
  `,
);

export default CopyStateToolbar;
